import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Globe, X } from "lucide-react";
import { applyLanguage, LANG_EXPLICIT_KEY } from "@/lib/applyLanguage";

// LanguageBanner — a discreet, dismissible bar shown ABOVE the beta banner
// to a visitor whose browser prefers a supported language OTHER than the one
// currently shown (PT by default). It only SUGGESTS a language — it never
// changes the content automatically — so the URL stays the single source of
// truth and SEO/canonicalisation is unaffected (crawlers keep seeing PT).

const DISMISS_KEY = "lang_suggest_dismissed";

// The offer is written in the TARGET language (standard UX for a "view in X"
// nudge shown to someone currently reading another language).
const OFFER = {
  en: { label: "View MotoTrips in English", cta: "Switch" },
  es: { label: "Ver MotoTrips en español", cta: "Cambiar" },
  fr: { label: "Voir MotoTrips en français", cta: "Changer" },
};

export function LanguageBanner() {
  const { i18n } = useTranslation();
  const ref = useRef(null);
  const [lng, setLng] = useState(null);

  // Decide whether to suggest a language (client-side only, never affects SEO).
  useEffect(() => {
    let suggestion = null;
    try {
      if (localStorage.getItem(DISMISS_KEY) === "1") return; // already dismissed
      if (localStorage.getItem(LANG_EXPLICIT_KEY)) return; // user already chose
      if (new URLSearchParams(window.location.search).get("lng")) return; // URL choice

      const cur = String(i18n.language || "pt").slice(0, 2).toLowerCase();
      if (cur !== "pt") return; // only nudge when currently showing PT

      const langs =
        navigator.languages && navigator.languages.length
          ? navigator.languages
          : [navigator.language || ""];
      for (const l of langs) {
        const code = String(l).slice(0, 2).toLowerCase();
        if (code === "pt") return; // browser prefers PT first → no nudge
        if (OFFER[code]) {
          suggestion = code;
          break;
        }
      }
    } catch (e) {
      return;
    }
    if (suggestion) setLng(suggestion);
  }, [i18n.language]);

  // Expose the bar height so the beta banner / header / content shift down.
  useEffect(() => {
    const setVar = (v) =>
      document.documentElement.style.setProperty("--lang-banner-h", v);
    if (!lng) {
      setVar("0px");
      return;
    }
    const update = () => setVar(`${ref.current?.offsetHeight || 0}px`);
    update();
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("resize", update);
      setVar("0px");
    };
  }, [lng]);

  if (!lng) return null;
  const offer = OFFER[lng];

  const accept = () => {
    applyLanguage(i18n, lng);
    setLng(null);
  };
  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, "1");
    } catch (e) {
      /* non-fatal */
    }
    setLng(null);
  };

  return (
    <div
      ref={ref}
      className="fixed top-0 left-0 right-0 z-[10001] bg-zinc-900/95 backdrop-blur-sm text-zinc-100 border-b border-white/10"
      data-testid="language-suggest-banner"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-1.5 flex items-center justify-center gap-3 text-xs sm:text-sm">
        <Globe className="h-3.5 w-3.5 text-primary flex-shrink-0" aria-hidden="true" />
        <span className="truncate">{offer.label}</span>
        <button
          onClick={accept}
          className="flex-shrink-0 rounded-full bg-primary px-3 py-0.5 text-xs font-semibold text-primary-foreground hover:bg-primary/90 transition-colors"
          data-testid="language-suggest-accept-btn"
        >
          {offer.cta}
        </button>
        <button
          onClick={dismiss}
          className="flex-shrink-0 p-1 rounded-full hover:bg-white/10 transition-colors"
          aria-label="Dismiss"
          data-testid="language-suggest-dismiss-btn"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

export default LanguageBanner;
